/**
 * Chart.js Configuration Utilities
 * Provides consistent theming, responsive options, and configuration for all charts
 */

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

/**
 * Application color palette based on theme.css
 */
export const CHART_COLORS = {
  primary: {
    light: "rgb(14, 165, 233)", // sky-500
    main: "rgb(2, 132, 199)", // sky-600
    dark: "rgb(3, 105, 161)", // sky-700
    alpha: "rgba(2, 132, 199, 0.8)",
  },
  secondary: {
    light: "rgb(132, 204, 22)", // lime-500
    main: "rgb(101, 163, 13)", // lime-600
    dark: "rgb(77, 124, 15)", // lime-700
    alpha: "rgba(101, 163, 13, 0.8)",
  },
  success: {
    light: "rgb(34, 197, 94)", // green-500
    main: "rgb(22, 163, 74)", // green-600
    dark: "rgb(21, 128, 61)", // green-700
    alpha: "rgba(22, 163, 74, 0.8)",
  },
  warning: {
    light: "rgb(251, 191, 36)", // amber-400
    main: "rgb(245, 158, 11)", // amber-500
    dark: "rgb(217, 119, 6)", // amber-600
    alpha: "rgba(245, 158, 11, 0.8)",
  },
  danger: {
    light: "rgb(248, 113, 113)", // red-400
    main: "rgb(239, 68, 68)", // red-500
    dark: "rgb(220, 38, 38)", // red-600
    alpha: "rgba(239, 68, 68, 0.8)",
  },
  info: {
    light: "rgb(96, 165, 250)", // blue-400
    main: "rgb(59, 130, 246)", // blue-500
    dark: "rgb(37, 99, 235)", // blue-600
    alpha: "rgba(59, 130, 246, 0.8)",
  },
  purple: {
    light: "rgb(168, 85, 247)", // purple-500
    main: "rgb(147, 51, 234)", // purple-600
    dark: "rgb(126, 34, 206)", // purple-700
    alpha: "rgba(147, 51, 234, 0.8)",
  },
  pink: {
    light: "rgb(244, 114, 182)", // pink-400
    main: "rgb(236, 72, 153)", // pink-500
    dark: "rgb(219, 39, 119)", // pink-600
    alpha: "rgba(236, 72, 153, 0.8)",
  },
  gray: {
    light: "rgb(156, 163, 175)", // gray-400
    main: "rgb(107, 114, 128)", // gray-500
    dark: "rgb(75, 85, 99)", // gray-600
    alpha: "rgba(107, 114, 128, 0.8)",
  },
};

/**
 * Color palette arrays for multi-dataset charts
 */
export const COLOR_PALETTE = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.success,
  CHART_COLORS.warning,
  CHART_COLORS.danger,
  CHART_COLORS.info,
  CHART_COLORS.purple,
  CHART_COLORS.pink,
  CHART_COLORS.gray,
];

/**
 * Get theme-aware colors based on current theme
 */
export function getThemeColors() {
  const isDark =
    document.documentElement.classList.contains("dark") ||
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  return {
    background: isDark ? "rgb(31, 41, 55)" : "rgb(255, 255, 255)",
    surface: isDark ? "rgb(55, 65, 81)" : "rgb(248, 250, 252)",
    onSurface: isDark ? "rgb(243, 244, 246)" : "rgb(51, 65, 85)",
    onSurfaceMuted: isDark ? "rgb(156, 163, 175)" : "rgb(100, 116, 139)",
    outline: isDark ? "rgb(75, 85, 99)" : "rgb(226, 232, 240)",
    outlineStrong: isDark ? "rgb(107, 114, 128)" : "rgb(148, 163, 184)",
  };
}

/**
 * Default responsive configuration
 */
export const RESPONSIVE_CONFIG = {
  responsive: true,
  maintainAspectRatio: false,
  resizeDelay: 100,
  devicePixelRatio: window.devicePixelRatio || 1,
};

/**
 * Mobile-optimized configuration
 */
export function getMobileConfig() {
  const isMobile = window.innerWidth < 768;

  return {
    plugins: {
      legend: {
        display: !isMobile || window.innerWidth > 480,
        position: isMobile ? "bottom" : "right",
        labels: {
          boxWidth: isMobile ? 12 : 16,
          padding: isMobile ? 10 : 20,
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
      tooltip: {
        titleFont: {
          size: isMobile ? 12 : 14,
        },
        bodyFont: {
          size: isMobile ? 11 : 13,
        },
        padding: isMobile ? 8 : 12,
      },
    },
    elements: {
      point: {
        radius: isMobile ? 3 : 4,
        hoverRadius: isMobile ? 5 : 6,
      },
      line: {
        borderWidth: isMobile ? 2 : 3,
      },
      bar: {
        borderWidth: isMobile ? 1 : 2,
      },
    },
  };
}

/**
 * Custom tooltip configuration
 */
export function getTooltipConfig(options = {}) {
  const themeColors = getThemeColors();

  return {
    backgroundColor: themeColors.surface,
    titleColor: themeColors.onSurface,
    bodyColor: themeColors.onSurface,
    borderColor: themeColors.outline,
    borderWidth: 1,
    cornerRadius: 8,
    displayColors: true,
    padding: 12,
    titleFont: {
      size: 14,
      weight: "600",
    },
    bodyFont: {
      size: 13,
    },
    footerFont: {
      size: 11,
      style: "italic",
    },
    caretPadding: 6,
    caretSize: 6,
    multiKeyBackground: themeColors.outline,
    ...options,
  };
}

/**
 * Custom legend configuration
 */
export function getLegendConfig(position = "bottom", options = {}) {
  const themeColors = getThemeColors();

  return {
    display: true,
    position: position,
    align: "center",
    labels: {
      usePointStyle: true,
      pointStyle: "circle",
      padding: 20,
      color: themeColors.onSurface,
      font: {
        size: 12,
        family: "Exo 2, sans-serif",
      },
      generateLabels: (chart) => {
        const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
        const labels = original.call(this, chart);

        // Add custom styling to labels
        labels.forEach((label, index) => {
          if (chart.data.datasets[index]) {
            label.fillStyle = chart.data.datasets[index].backgroundColor;
            label.strokeStyle = chart.data.datasets[index].borderColor;
            label.lineWidth = 2;
          }
        });

        return labels;
      },
    },
    onClick: (event, legendItem, legend) => {
      const index = legendItem.datasetIndex;
      const chart = legend.chart;
      const meta = chart.getDatasetMeta(index);

      meta.hidden =
        meta.hidden === null ? !chart.data.datasets[index].hidden : null;
      chart.update();
    },
    onHover: (event, legendItem, legend) => {
      event.native.target.style.cursor = "pointer";
    },
    onLeave: (event, legendItem, legend) => {
      event.native.target.style.cursor = "default";
    },
    ...options,
  };
}

/**
 * Scale configuration for different chart types
 */
export function getScaleConfig(type = "linear", options = {}) {
  const themeColors = getThemeColors();

  const baseConfig = {
    grid: {
      color: themeColors.outline,
      lineWidth: 1,
      drawBorder: true,
      drawOnChartArea: true,
      drawTicks: true,
    },
    ticks: {
      color: themeColors.onSurfaceMuted,
      font: {
        size: 11,
        family: "Exo 2, sans-serif",
      },
      padding: 8,
    },
    title: {
      display: false,
      color: themeColors.onSurface,
      font: {
        size: 12,
        weight: "600",
        family: "Quicksand, sans-serif",
      },
    },
  };

  switch (type) {
    case "linear":
      return {
        ...baseConfig,
        beginAtZero: true,
        grace: "5%",
        ...options,
      };

    case "category":
      return {
        ...baseConfig,
        grid: {
          ...baseConfig.grid,
          display: false,
        },
        ...options,
      };

    case "time":
      return {
        ...baseConfig,
        type: "time",
        time: {
          displayFormats: {
            day: "MMM DD",
            week: "MMM DD",
            month: "MMM YYYY",
            quarter: "MMM YYYY",
            year: "YYYY",
          },
        },
        ...options,
      };

    default:
      return { ...baseConfig, ...options };
  }
}

/**
 * Animation configuration
 */
export const ANIMATION_CONFIG = {
  duration: 750,
  easing: "easeInOutQuart",
  delay: (context) => {
    let delay = 0;
    if (context.type === "data" && context.mode === "default") {
      // Calculate total data points to determine if we should disable staggered animation
      const chart = context.chart;
      const totalDataPoints = chart.data.datasets.reduce((sum, dataset) => {
        return sum + (dataset.data?.length || 0);
      }, 0);

      // Disable staggered animation for large datasets (>20 points)
      if (totalDataPoints > 20) {
        return 0;
      }

      // For smaller datasets, use reduced stagger with a cap
      const baseDelay = Math.min(context.dataIndex * 30, 500); // Cap at 500ms
      const datasetDelay = Math.min(context.datasetIndex * 50, 200); // Cap at 200ms
      delay = baseDelay + datasetDelay;
    }
    return delay;
  },
  onComplete: () => {
    // Animation complete callback
  },
  onProgress: () => {
    // Animation progress callback
  },
};

/**
 * Interaction configuration
 */
export const INTERACTION_CONFIG = {
  intersect: false,
  mode: "index",
  includeInvisible: false,
};

/**
 * Get complete chart configuration for specific chart types
 */
export function getChartConfig(type, data, customOptions = {}) {
  const themeColors = getThemeColors();
  const mobileConfig = getMobileConfig();

  const baseConfig = {
    type: type,
    data: data,
    options: {
      ...RESPONSIVE_CONFIG,
      ...mobileConfig,
      plugins: {
        ...mobileConfig.plugins,
        legend: getLegendConfig(),
        tooltip: getTooltipConfig(),
        ...customOptions.plugins,
      },
      interaction: INTERACTION_CONFIG,
      animation: ANIMATION_CONFIG,
      backgroundColor: themeColors.background,
      borderColor: themeColors.outline,
      color: themeColors.onSurface,
      ...customOptions,
    },
  };

  // Add type-specific configurations
  switch (type) {
    case "bar":
      baseConfig.options.scales = {
        x: getScaleConfig("category"),
        y: getScaleConfig("linear"),
      };
      break;

    case "line":
      baseConfig.options.scales = {
        x: getScaleConfig("category"),
        y: getScaleConfig("linear"),
      };
      baseConfig.options.elements = {
        line: {
          tension: 0.4,
          borderWidth: 3,
          fill: false,
        },
        point: {
          radius: 4,
          hoverRadius: 6,
          borderWidth: 2,
        },
      };
      break;

    case "doughnut":
    case "pie":
      baseConfig.options.cutout = type === "doughnut" ? "60%" : "0%";
      if (baseConfig.options.plugins.legend) {
        baseConfig.options.plugins.legend.position = "right";
      }
      delete baseConfig.options.scales;
      break;

    case "radar":
      baseConfig.options.scales = {
        r: {
          ...getScaleConfig("linear"),
          angleLines: {
            color: themeColors.outline,
          },
          grid: {
            color: themeColors.outline,
          },
          pointLabels: {
            color: themeColors.onSurface,
            font: {
              size: 11,
            },
          },
        },
      };
      break;

    case "polarArea":
      baseConfig.options.scales = {
        r: getScaleConfig("linear"),
      };
      break;
  }

  return baseConfig;
}

/**
 * Generate color scheme for datasets
 */
export function generateDatasetColors(datasetCount, colorType = "varied") {
  const colors = [];

  for (let i = 0; i < datasetCount; i++) {
    const colorSet = COLOR_PALETTE[i % COLOR_PALETTE.length];

    switch (colorType) {
      case "monochrome":
        // Use different shades of primary color
        const opacity = 0.8 - i * 0.1;
        colors.push({
          backgroundColor: `rgba(2, 132, 199, ${Math.max(opacity, 0.3)})`,
          borderColor: CHART_COLORS.primary.main,
          hoverBackgroundColor: `rgba(2, 132, 199, ${Math.min(opacity + 0.2, 1)})`,
          hoverBorderColor: CHART_COLORS.primary.dark,
        });
        break;

      case "gradient":
        // Create gradient colors
        colors.push({
          backgroundColor: colorSet.alpha,
          borderColor: colorSet.main,
          hoverBackgroundColor: colorSet.main,
          hoverBorderColor: colorSet.dark,
        });
        break;

      default:
        // Varied colors
        colors.push({
          backgroundColor: colorSet.alpha,
          borderColor: colorSet.main,
          hoverBackgroundColor: colorSet.main,
          hoverBorderColor: colorSet.dark,
        });
    }
  }

  return colors;
}

/**
 * Update chart theme when theme changes
 */
export function updateChartTheme(chart) {
  if (!chart) return;

  const themeColors = getThemeColors();

  // Update chart options
  chart.options.plugins.tooltip = {
    ...chart.options.plugins.tooltip,
    ...getTooltipConfig(),
  };

  chart.options.plugins.legend.labels.color = themeColors.onSurface;

  // Update scales if they exist
  if (chart.options.scales) {
    Object.keys(chart.options.scales).forEach((scaleKey) => {
      const scale = chart.options.scales[scaleKey];
      scale.grid.color = themeColors.outline;
      scale.ticks.color = themeColors.onSurfaceMuted;
      if (scale.title) {
        scale.title.color = themeColors.onSurface;
      }
    });
  }

  chart.update("none");
}

/**
 * Export chart utilities
 */
export default {
  CHART_COLORS,
  COLOR_PALETTE,
  getThemeColors,
  RESPONSIVE_CONFIG,
  getMobileConfig,
  getTooltipConfig,
  getLegendConfig,
  getScaleConfig,
  ANIMATION_CONFIG,
  INTERACTION_CONFIG,
  getChartConfig,
  generateDatasetColors,
  updateChartTheme,
};
